import { SpanStatus, getTraceData } from '@sentry/core'
import * as Sentry from '@sentry/react'
import { TELECOM_CLIENT_GRAPHQL } from '../config'
import useAuth from '../hooks/useAuth'
import { logout } from '../utils/localStorage'

type HeaderType = RequestInit['headers'] & {
  'Content-Type': string
  authorization?: string
  'sentry-trace'?: string
  'baggage'?: string
}

export const useFetchData = <TData, TVariables>(
  query: string,
  options?: RequestInit['headers'],
): ((variables?: TVariables) => Promise<TData>) => {
  const { authInformation } = useAuth()

  const headers: HeaderType = {
    'Content-Type': 'application/json',
    ...options,
  }

  if (authInformation?.token) {
    headers['authorization'] = `bearer ${authInformation.token}`
  }

  return async (variables?: TVariables) => {
    const operationName = extractOperationName(query)
    const operationType = query.trim().startsWith('mutation') ? 'mutation' : 'query'

    return Sentry.startSpanManual(
      {
        name: `Telecom GraphQL: ${operationType} ${operationName}`,
        op: 'http.client',
        attributes: {
          operation_type: operationType,
          operation_name: operationName,
        },
      },
      async (span, finish) => {
        try {
          // Sentry tracing headers for distributed traces between frontend and backend e2e
          const dynamicHeaders = { ...headers }
          const traceData = getTraceData()
          const sentryTraceHeader = traceData['sentry-trace']
          const sentryBaggageHeader = traceData['baggage']

          if (sentryTraceHeader) {
            dynamicHeaders['sentry-trace'] = sentryTraceHeader
          }

          if (sentryBaggageHeader) {
            dynamicHeaders['baggage'] = sentryBaggageHeader
          }

          span.setAttribute('app', 'Telecom GraphQL')

          const requestBody = JSON.stringify({ query, variables })
          span.setAttribute('request.size_bytes', new Blob([requestBody]).size)

          const fetchStartTime = performance.now()

          const res = await fetch(TELECOM_CLIENT_GRAPHQL, {
            method: 'POST',
            headers: dynamicHeaders,
            body: requestBody,
          })

          if (!res.ok) {
            const statusMap = {
              401: { code: 2, message: 'unauthenticated' },
              403: { code: 2, message: 'permission_denied' },
              404: { code: 2, message: 'not_found' },
              409: { code: 2, message: 'already_exists' },
              429: { code: 2, message: 'resource_exhausted' },
              500: { code: 2, message: 'internal_error' },
              501: { code: 2, message: 'unimplemented' },
              503: { code: 2, message: 'unavailable' },
              'unknown': { code: 2, message: 'unknown_error' },
            } as Record<string, SpanStatus>

            span.setStatus(statusMap[res.status] || statusMap.unknown)

            Sentry.captureMessage(`HTTP error ${res.status}`, {
              level: 'error',
              tags: {
                endpoint: TELECOM_CLIENT_GRAPHQL,
                operation: operationName,
                status_code: res.status.toString(),
              },
            })
          }

          let json
          try {
            json = await res.json()

            span.setAttribute('response.size_bytes', new Blob([JSON.stringify(json)]).size)
          } catch (error) {
            span.setStatus({ code: 2, message: 'invalid_argument' })

            Sentry.captureException(error, {
              tags: {
                error_type: 'json_parse_error',
                endpoint: 'graphql',
              },
            })
            throw new Error('Received an invalid response from the server. Please try again later.')
          }

          if (
            json.errors?.[0]?.extensions?.code === 'UNAUTHENTICATED' ||
            json.errors?.[0]?.extensions?.exception?.status === 401
          ) {
            span.setStatus({ code: 2, message: 'unauthenticated' })

            try {
              const userData = localStorage.getItem('user')
              const errorCode = json.errors?.[0]?.extensions?.code
              const errorStatus = json.errors?.[0]?.extensions?.exception?.status

              Sentry.captureMessage('Authentication failure', {
                level: 'warning',
                tags: {
                  error_type: 'auth_error',
                  auth_error_code: errorCode || 'unknown',
                  status_code: errorStatus || 'unknown',
                },
                extra: {
                  user_data: userData ? JSON.parse(userData) : 'No user data found',
                  error_details: json.errors?.[0] || {},
                  operation: operationName,
                },
              })
            } catch (e) {
              Sentry.captureMessage('Authentication failure - could not read user data')
            }

            logout()
          }

          if (json.errors) {
            span.setStatus({ code: 2, message: 'unknown_error' })

            const { message } = json.errors[0] || {}
            throw new Error(message || 'Error…')
          }

          span.setAttribute('total.time_ms', performance.now() - fetchStartTime)

          span.setStatus({ code: 1, message: 'ok' })

          return json.data
        } catch (error) {
          span.setStatus({ code: 2, message: 'internal_error' })
          throw error
        } finally {
          finish()
        }
      }
    )
  }
}

function extractOperationName(query: string): string {
  const operationMatch = query.match(/(?:query|mutation|subscription)\s+(\w+)/)

  if (operationMatch && operationMatch[1]) {
    return operationMatch[1]
  }

  if (query.trim().startsWith('mutation')) {
    return 'AnonymousMutation'
  } else if (query.trim().startsWith('subscription')) {
    return 'AnonymousSubscription'
  } else {
    return 'AnonymousQuery'
  }
}
